import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { CheckResponseTypes, StatusResponseTypes } from '../constants/event';
import { IStatusTransmissionMessage } from '../reducers/messagingSlice';
import {
  LoyaltyResponseStatus,
  ORDER_PENDING_ERROR_CODE,
  OrderStatus,
  TERMINAL_ORDER_STATUS_CODES,
} from '../reducers/orderSlice.constants';
import { selectCartItemsQuantity, selectValidCartItems } from './cart';
import { getSpokenTotalCartFingerprint } from '../utils/cart';
import { orderTotalToCents } from '../utils/TTSMessage';
import { ICartError } from '../types/brandMenu';

const selectOrder = (state: RootState) => state.order;

const selectOrderValues = createSelector(
  selectOrder,
  ({ subtotal, tax, total }) => ({
    subtotal: orderTotalToCents(subtotal),
    tax: orderTotalToCents(tax),
    total: orderTotalToCents(total),
  })
);

const selectCurrentTransactionId = createSelector(
  selectOrder,
  (order) => order.currentTransactionId
);
const selectTransactions = createSelector(
  selectOrder,
  (order) => order.transactions
);
const selectOrderError = createSelector(
  selectOrder,
  (order) => order.orderError
);
const selectIsPosmonDisconnected = createSelector(
  selectOrder,
  (order) => order.isPosmonDisconnected
);

const selectIsPosmonReconnected = createSelector(
  selectOrder,
  (order) => order.isPosmonReconnected
);

const selectCurrentTransactionCoupons = createSelector(
  selectOrder,
  (order) => order.currentTransactionCoupons
);

const selectCurrentTransactionLoyalties = createSelector(
  selectOrder,
  (order) => order.currentTransactionLoyalties
);

// The most recent order request (the only one whose check event is processed —
// see the orderSlice `messageReceived` handler), or undefined when none exist.
const selectLatestOrderRequest = createSelector(selectOrder, (order) => {
  const lastRequestId = order.requestsOrder.length
    ? order.requestsOrder[order.requestsOrder.length - 1]
    : '';
  return lastRequestId ? order.requestsById[lastRequestId] : undefined;
});

// The order id needing session-end cleanup: useSessionStatus cancels this
// order when the session ends without a submitted final. A FINAL request
// never qualifies — the agent deliberately finished; cancelling it would void
// an order the POS may have already closed. On master this exclusion was
// accidental (finishOrderResetValues wiped requestsById, so the id was
// undefined after a finish); since HITL-1527 preserves the final request for
// the status banner, the exclusion must be explicit or the session-end
// cancel_order re-arms against confirmed finals.
const selectLastHITLOrderId = createSelector(
  selectLatestOrderRequest,
  (latestRequest) =>
    latestRequest?.isFinal ? undefined : latestRequest?.orderId
);

const selectOrderErrorCartItemId = createSelector(
  selectOrder,
  (order) => order.errorCartItemId
);

const selectMenuModuleCartErrors = createSelector(
  selectOrder,
  (order) => order.menuModuleCartErrors
);

/**
 * HITL-1699: the menu-module errors, but only while they describe the latest
 * send — [] once a newer send is in flight (each cart change sends at once), so
 * the Omnibar never offers a line about a cart the agent has already changed.
 * The newer send's response then sets fresh errors or clears them. The cart's
 * error highlights keep reading `selectMenuModuleCartErrors`.
 */
const NO_CART_ERRORS: ICartError[] = [];
const selectCurrentMenuModuleCartErrors = createSelector(
  selectOrder,
  (order): ICartError[] =>
    order.menuModuleCartErrorsSeqId === order.menuModuleRequestSeqId
      ? order.menuModuleCartErrors
      : NO_CART_ERRORS
);

/**
 * HITL-1699: the POS rejected the last send (an order_status or check error
 * from POSMON/POSLINK, or a send that never got a response) — '' when not.
 * Exactly the condition under which a cart line shows its red "failed" alert
 * without a menu-module error: the error line is still `failed`, and a
 * loyalty-code rejection is excluded (it has its own status, not an order
 * failure). A resend marks the line `sending` again, which retires it.
 */
const selectPosOrderFailure = createSelector(selectOrder, (order): string => {
  const { orderError, errorCartItemId, currentTransactionItems } = order;
  if (!orderError || orderError === LoyaltyResponseStatus.invalid) return '';
  if (order.menuModuleCartErrors?.length) return '';
  if (
    errorCartItemId === undefined ||
    currentTransactionItems?.[errorCartItemId] !== OrderStatus.failed
  ) {
    return '';
  }
  return orderError;
});

const selectFinalOrderStatus = createSelector(
  selectOrder,
  (order) => order.finalOrderStatus
);

// Get check event items from the order state
const selectCheckEventItems = createSelector(
  selectOrder,
  (order) => order.checkEventItems
);

// Get customer name from the order state
const selectCustomerName = createSelector(
  selectOrder,
  (order) => order.customerName
);

// The one place the order pipeline's state is derived (HITL-1527). Every
// surface that answers "what is the POS doing with the latest order?" — the
// Order Status Banner today, metrics/tooltips later — reads this instead of
// re-deriving from request fields, so their readings cannot diverge.
export enum OrderPipelineState {
  // The menu module rejected the latest send attempt (e.g. unavailable items)
  // — the order never reached the POS, so the pipeline fails at the SENT
  // stage. Painting anything POS-side would claim the POS knows about an
  // order it never received; the POS still holds the previous order. Cleared
  // by resetMenuModuleErrorState on the next successful translation.
  sendFailed = 'SEND_FAILED',
  // Order event out; no POS response yet. Also covers a 7002 "Send Order is
  // Pending" order_status — POSLINK dropped that send, a later one (or the
  // Retry) supersedes it, so the pipeline is still "waiting", matching the
  // mask_order_pending_error semantics and the retry clock (which an
  // order_status never stops).
  sending = 'SENDING',
  // order_status success; the check has not arrived yet.
  acked = 'ACKED',
  // The check arrived and is ok — the POS has the order.
  confirmed = 'CONFIRMED',
  // The check arrived with an error, or a non-retryable order_status error.
  failed = 'FAILED',
  // A TERMINAL order_status code (7008 "Final order already received"): no
  // resend of this session's order can ever succeed.
  rejected = 'REJECTED',
  // POSMON reported a disconnect; the reconnect flow resends on `connect`.
  disconnected = 'DISCONNECTED',
}

export interface IOrderPipeline {
  state: OrderPipelineState;
  // How far the round trip got, whatever its health: 1 = the order event went
  // out, 2 = the POS answered with an order_status, 3 = the check arrived.
  // Drives the banner ring's fill fraction — including for failures, which
  // paint the reached stage red.
  stage: 1 | 2 | 3;
  isFinal: boolean;
  // Who submitted the latest order — the banner's origin badge. Defaults to
  // 'AGENT' when untagged (pre-existing requests in flight during rollout).
  origin: 'AI' | 'AGENT';
  requestId: string;
  // Client receipt/send time of the order event; the banner derives its live
  // elapsed from this. (Server-stamped order_event_at replaces it when
  // HITL-1513's metric anchoring lands.)
  sentAt?: string;
  // The latest check total (dollars string), for the confirmed state.
  total: string;
}

// Time-independent on purpose: "stuck" is a *duration* judgment and lives with
// the overdue clock in useOrderCheckRetry — a selector re-deriving Date.now()
// would never re-run on its own anyway.
const selectOrderPipeline = createSelector(
  selectLatestOrderRequest,
  selectIsPosmonDisconnected,
  selectOrder,
  (state: RootState) => state.cart.cartItems,
  (
    latestRequest,
    isPosmonDisconnected,
    order,
    cartItems
  ): IOrderPipeline | null => {
    // A menu-module rejection is the newest fact about the newest attempt —
    // it outranks whatever the last POS-reaching request was doing, and it
    // can exist before any request does (a first attempt that never sent).
    // The cart guard mirrors OrderErrorSummary's: an emptied cart has nothing
    // the errors could refer to, so they stop driving the pipeline.
    if (
      order.menuModuleCartErrors.length > 0 &&
      Object.keys(cartItems || {}).length > 0
    ) {
      return {
        isFinal: false,
        stage: 1 as const,
        origin: latestRequest?.origin ?? 'AGENT',
        requestId: latestRequest?.id ?? '',
        sentAt: undefined,
        total: order.total,
        state: OrderPipelineState.sendFailed,
      };
    }
    if (!latestRequest) {
      return null; // No order sent this session yet — no pipeline to report.
    }
    // A 7002 "Send Order is Pending" order_status does NOT count as reaching
    // stage 2: POSLINK dropped that send, so the pipeline is still "sending" —
    // the same masking semantics the state below applies.
    const stage: 1 | 2 | 3 = latestRequest.checkResponse
      ? 3
      : latestRequest.orderStatusResponse &&
        latestRequest.orderStatusResponse?.data?.status?.code !==
          ORDER_PENDING_ERROR_CODE
      ? 2
      : 1;
    const base = {
      isFinal: latestRequest.isFinal,
      stage,
      origin: latestRequest.origin ?? ('AGENT' as const),
      requestId: latestRequest.id,
      sentAt: latestRequest.orderSentAt,
      total: order.total,
    };

    if (isPosmonDisconnected) {
      return { ...base, state: OrderPipelineState.disconnected };
    }
    if (latestRequest.checkResponse) {
      const checkStatus = latestRequest.checkResponse?.data?.status;
      return {
        ...base,
        state:
          checkStatus === CheckResponseTypes.ok
            ? OrderPipelineState.confirmed
            : OrderPipelineState.failed,
      };
    }
    const orderStatus = latestRequest.orderStatusResponse?.data?.status;
    if (orderStatus) {
      if (TERMINAL_ORDER_STATUS_CODES.includes(orderStatus.code)) {
        return { ...base, state: OrderPipelineState.rejected };
      }
      if (orderStatus.type === StatusResponseTypes.success) {
        return { ...base, state: OrderPipelineState.acked };
      }
      if (orderStatus.code !== ORDER_PENDING_ERROR_CODE) {
        return { ...base, state: OrderPipelineState.failed };
      }
    }
    return { ...base, state: OrderPipelineState.sending };
  }
);

// Which leg of the round trip an outstanding final order is on. These are the
// same three spans ORDER_PROCESSING_METRIC records, so what the agent sees and
// what the dashboard logs decompose the delay identically.
export type FinalOrderLeg = 'sending' | 'confirming' | 'rejected';

export interface IFinalOrderState {
  // The leg, or null when no final order is outstanding. Null means render
  // nothing — it is the "there is nothing to say" case.
  leg: FinalOrderLeg | null;
  // True only while POS still owes an answer ('sending' | 'confirming').
  // Deliberately FALSE on 'rejected': that is terminal, and re-sending is then
  // the correct recovery rather than a duplicate. Anything that warns the agent
  // off the Finish Order button must read this, not `leg !== null`.
  inFlight: boolean;
  requestId: string | null;
  // Client-stamped send time. Only ever compared against Date.now(), which is
  // the same clock, so elapsed is internally consistent even on a skewed
  // machine (unlike the metric, which mixes clocks — hence HITL-1513 part A).
  sentAt: string | null;
  // POSLINK's own reason, on the 'rejected' leg only.
  error: string | null;
}

const NO_FINAL_ORDER: IFinalOrderState = {
  leg: null,
  inFlight: false,
  requestId: null,
  sentAt: null,
  error: null,
};

// HITL-1513 (C): is an AI-submitted final order still outstanding?
//
// Derived, never stored. Every field it reads already exists on the request
// entry, so this cannot latch (it disappears with `requestsById`, which every
// reset path already clears) and cannot desync (there is no second copy of the
// truth to drift from). Above all it CONTROLS NOTHING: the worst a wrong answer
// can do is show a misleading banner. That property is the whole design — the
// previous attempt at this fact (`final_order_validation`) put it in the
// session-teardown path behind a blocking dialog, and had to be disabled in
// production. Nothing here writes state, dispatches, or gates a code path.
//
// Scope: any final — AI- or HITL-submitted. finishOrderResetValues used to
// erase a HITL final's entry synchronously (which had scoped this to AI
// finals); since HITL-1527 the reset preserves the final request, so this
// covers the agent's own finals too.
const selectFinalOrderInFlight = createSelector(
  selectLatestOrderRequest,
  (latestRequest): IFinalOrderState => {
    if (!latestRequest?.isFinal || latestRequest.checkResponse) {
      return NO_FINAL_ORDER;
    }

    const { data } =
      (latestRequest.orderStatusResponse as IStatusTransmissionMessage) || {};
    const { type = '', message = '' } = data?.status || {};

    // Only reachable for a final order, and the 7002 masking in
    // processOrderStatusAndCheckResponses explicitly excludes finals — so any
    // error type here is a real rejection, with no feature flag to consult.
    const isRejected = type === StatusResponseTypes.error;

    let leg: FinalOrderLeg = 'sending';
    if (isRejected) leg = 'rejected';
    else if (latestRequest.orderStatusResponse) leg = 'confirming';

    return {
      leg,
      inFlight: !isRejected,
      requestId: latestRequest.id ?? null,
      sentAt: latestRequest.orderSentAt ?? null,
      error: isRejected ? message || null : null,
    };
  }
);

// HITL-1570: did the donation upsell prompt already speak the check total?
const selectTotalSpokenInUpsell = createSelector(
  selectOrder,
  (order) => order.totalSpokenInUpsell
);

// HITL-1570: the spoken total is only honored while the cart still matches
// the fingerprint captured when it was spoken. Donation-item changes (accept
// or decline) are excluded from the fingerprint, so they don't re-arm the
// readback; any other cart change after the ask does, and Complete falls back
// to the normal total readback.
const selectIsTotalSpokenInUpsellValid = createSelector(
  selectTotalSpokenInUpsell,
  createSelector(
    selectOrder,
    (order) => order.totalSpokenInUpsellCartFingerprint
  ),
  selectValidCartItems,
  selectCartItemsQuantity,
  (spoken, fingerprint, validCartItems, cartItemsQuantity) => {
    if (!spoken) {
      return false;
    }
    const current = getSpokenTotalCartFingerprint(
      validCartItems,
      cartItemsQuantity
    );
    const stored = fingerprint ?? [];
    return (
      stored.length === current.length &&
      stored.every((entry, i) => entry === current[i])
    );
  }
);

export {
  selectOrder,
  selectOrderValues,
  selectTotalSpokenInUpsell,
  selectIsTotalSpokenInUpsellValid,
  selectCurrentTransactionId,
  selectTransactions,
  selectOrderError,
  selectIsPosmonDisconnected,
  selectIsPosmonReconnected,
  selectCurrentTransactionCoupons,
  selectCurrentTransactionLoyalties,
  selectLatestOrderRequest,
  selectLastHITLOrderId,
  selectOrderErrorCartItemId,
  selectMenuModuleCartErrors,
  selectPosOrderFailure,
  selectCurrentMenuModuleCartErrors,
  selectFinalOrderStatus,
  selectCheckEventItems,
  selectCustomerName,
  selectOrderPipeline,
  selectFinalOrderInFlight,
};
